import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { getInsightBySlug } from "@/lib/insights";
import { trackBudgetRequest, trackInsightView } from "@/lib/analytics";
import BreadcrumbTrail from "@/components/BreadcrumbTrail";

const InsightArticlePage = () => {
  const { slug } = useParams();
  const article = getInsightBySlug(slug);

  useEffect(() => {
    if (article) trackInsightView(article.slug);
  }, [article]);

  if (!article) return <Navigate to="/insights" replace />;

  return (
    <main id="main-content" className="bg-[#f8f5ee] pt-24">
      <article>
        <header className="relative overflow-hidden bg-[#ff6b35] noise py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 relative">
            <BreadcrumbTrail items={[{ label: "Insights", href: "/insights" }, { label: article.h1 }]} />
            <div className="inline-block bg-[#0a0a0a] text-white px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-5">
              {article.category} · {article.readingTime}
            </div>
            <h1 className="font-display text-[#0a0a0a] text-[2.55rem] min-[380px]:text-5xl sm:text-7xl uppercase leading-[0.9] max-w-5xl">
              {article.h1}
            </h1>
            <p className="mt-6 max-w-3xl text-[#0a0a0a]/75 text-lg sm:text-xl leading-snug">
              {article.summary}
            </p>
            <p className="mt-5 font-mono text-xs uppercase tracking-widest text-white">
              {article.author} · Atualizado em {article.updatedAt}
            </p>
          </div>
        </header>

        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-8">
          <aside className="lg:sticky lg:top-28 self-start bg-white brutal-border p-5">
            <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-4">
              Neste artigo
            </p>
            <ol className="space-y-3">
              {article.tableOfContents.map((item) => (
                <li key={item} className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]/75">
                  {item}
                </li>
              ))}
            </ol>
          </aside>

          <div className="space-y-8">
            {article.sections.map((section) => (
              <section key={section.title} className="bg-white brutal-border p-6">
                <h2 className="font-display text-[#0a0a0a] text-3xl sm:text-4xl uppercase leading-none">
                  {section.title}
                </h2>
                <p className="mt-5 text-[#0a0a0a]/75 text-lg leading-snug">
                  {section.body}
                </p>
              </section>
            ))}

            <section className="bg-[#0a0a0a] brutal-border p-6">
              <h2 className="font-display text-white text-3xl sm:text-4xl uppercase leading-none">
                Perguntas frequentes
              </h2>
              <div className="mt-6 space-y-5">
                {article.faq.map((item) => (
                  <div key={item.question} className="border-t-4 border-[#ff6b35] pt-4">
                    <h3 className="font-display text-[#ff6b35] text-2xl uppercase leading-none">
                      {item.question}
                    </h3>
                    <p className="mt-3 text-white/75 leading-snug">
                      {item.answer}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-white brutal-border p-6">
              <h2 className="font-display text-[#0a0a0a] text-3xl sm:text-4xl uppercase leading-none">
                Próximo passo
              </h2>
              <p className="mt-4 text-[#0a0a0a]/75 leading-snug">
                {article.cta.text}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <Link
                  to={article.cta.href}
                  onClick={() => trackBudgetRequest(`insight_${article.slug}`)}
                  className="btn-brutal btn-brutal-orange"
                >
                  <span>{article.cta.label}</span>
                  <ArrowRight size={18} className="shrink-0" />
                </Link>
                {article.internalLinks.slice(0, 2).map((link) => (
                  <Link key={link.href} to={link.href} className="btn-brutal">
                    {link.label}
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>
    </main>
  );
};

export default InsightArticlePage;
